import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { MenuItemContainer } from "@cervval.js/client-html5-core/src/components/menuItemContainer";
import { Icon, buildIcon } from "@cervval.js/client-html5-core/src/components/models/icon";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { Display, FaClasses, toggleClass, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { REF_ATTRIBUTE, createHtmlElement, empty, setHTMLElementStyle, toggleAttribute } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { getProp, setProp } from "@cervval.js/common/src/tools/any.tools";
import { toPx } from "@cervval.js/common/src/tools/number.tools";
import { ValueOrFunction, getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { Dropdown } from "../../bootstrap";
import { BTN_CLASS, BTN_CLASS_SEPARATOR, BTN_OUTLINE, BTN_RAISED, BTN_ROUNDED, ButtonOptions, ButtonSize, ButtonStyle, CjsButton, defaultButtonOptions } from "../button/button";
import { FA_FW, IconFaSize, getIconFaName } from "../icon/iconFa.tools";
import { IconFA6 } from "../icon/tools/fontAwesome6IconList.tools";

export type DropdownButtonItemClickEvent<ItemType = string> = {

    /** Value of item clicked */
    value?: ItemType
}

/** Possible horizontal directions */
export type DropdownButtonHorizontalDirection = "left" | "right";

/** Possible directions */
export type DropdownButtonDirection = DropdownButtonHorizontalDirection | "top" | "bottom";

export interface DropdownButtonOptions<ItemType = string> extends ButtonOptions {

    /** Add arrow of dropdown */
    arrow: boolean;

    /** Bootstrap dropdown Options */
    bsDropDrownOptions: Partial<Dropdown.Options>;

    /** Set menu dark mode */
    darkMenu: boolean;

    /** Trigger dropdown menus at the "top" | "left" | "right" | "bottom" of the button */
    dropDirection?: DropdownButtonDirection;

    /** Dropdown items of button */
    dropDownItemConfigs: DropdownItemConfig<ItemType>[];

    /** Align the dropDown menu (the container, not the text) with the "left" | "right" of the button */
    dropDownMenuAlign?: DropdownButtonHorizontalDirection;

    /** Change the content of the dropdown button with the content of the last clicked item */
    enableSelectBehavior: boolean;

    /** DropDown button only display the icon of selected dropdown item */
    iconOnly: boolean;

    /** 
     * Dropdown menu will be opened in document body (used to over all other elements)
     * ! With this option enable, menu is fixed on page, and will NOT scroll with button !
     */
    openMenuInBody?: boolean;

    /** Trigger sub dropdown menus at the "left" | "right" of the dropDown menu */
    subMenuDropDirection: DropdownButtonHorizontalDirection;

    /** Split toggle on arrow */
    splitToggleArrow: boolean;

    /** 
     * Function used to create hierarchy for sub-menu of dropdownItems
     * From a childItem, return his parent (you can use value, refAttribute, etc.)
     */
    getDropdownItemParent?: (childItem: DropdownItemConfig<ItemType>) => DropdownItemConfig<ItemType> | undefined;

}

export const defaultDropdownButtonOptions: DropdownButtonOptions<string> = {

    /** Inherit options of button */
    ...defaultButtonOptions,

    /** Add arrow of dropdown */
    arrow: true,

    /** Bootstrap dropDown Options */
    bsDropDrownOptions: Dropdown.Default,

    /** Set menu dark mode */
    darkMenu: false,

    /** Trigger dropdown menus at the "top" | "left" | "right" | "bottom" of the button */
    dropDirection: undefined,

    /** Dropdown items of button */
    dropDownItemConfigs: [],

    /** DropDown menu will be align with the "left" | "right" border of the button */
    dropDownMenuAlign: "left",

    /** Change the label of the dropdown button with the label of the last clicked item */
    enableSelectBehavior: false,

    /** DropDown button only display the icon of selected dropdown item */
    iconOnly: false,

    /** Trigger submenu dropdown at the "right" | "left" of the dropDown menu */
    subMenuDropDirection: "right",

    /** Split toggle on arrow */
    splitToggleArrow: false
};

export interface DropdownItemConfig<ValueType = string> {

    /** Label of item */
    label: string;

    /** Add a separator after item */
    separator?: boolean;

    /** Value of the item, can be anything, needs to correspond to ItemType */
    value?: ValueOrFunction<ValueType>;

    /** Icon of item */
    icon?: Icon;

    /** To override the default expand Icon () */
    subMenuIcon?: Icon;

    /** Disable item */
    disabled?: ValueOrFunction<boolean>;

    /** Hide item */
    hide?: ValueOrFunction<boolean>;

    /** Ref attribute of the item */
    refAttribute?: string;

    /** Ref of the parent item */
    parentRef?: string;

    /** Optionnals css classes to add */
    classes?: string[];

    /** Item tooltip */
    title?: string;

    href?: string;
}

export interface DropdownItem<ValueType = string> extends DropdownItemConfig<ValueType> {

    /** Depth of dropdown item in hierarchy */
    depth: number;

    /** Generated from the build contains children items */
    dropDownItems: DropdownItem<ValueType>[];

    /** Generated from the build DOM element*/
    html?: HTMLLIElement;
}

const DROPDOWN_CONFIG_CHILD_ATTRIBUTE = "__childConfigs";
const DROPDOWN_CONFIG_PARENT_ATTRIBUTE = "__hasParent";

@CustomElement({
    name: "cjs-dropdown-button",
    style: require("./dropdownButton.scss"),
    template: require("./dropdownButton.html"),
    elementCssClasses: ["dropdown", "cjs-button"]
})
export class CjsDropdownButton<ValueType = string> extends CjsButton implements MenuItemContainer {

    /** 
     * Component HTML elements
     */
    @TypedHtmlElements()
    public override readonly components!: {
        button: HTMLButtonElement,
        splitButton: HTMLButtonElement,
        dropdownMenu: HTMLUListElement,
        icon: HTMLElement
    };

    @TypedWebComponentOptions(defaultDropdownButtonOptions)
    public override readonly options!: WebComponentOptions<DropdownButtonOptions<ValueType>>;

    /** On item of dropdown button clicked */
    @Event(NoBubblesEventInit)
    public itemClickEvent!: EventHandler<DropdownButtonItemClickEvent<ValueType>>;

    /**
     * Dropdown items generated, don't expose setter for theses items
     */
    private _dropDownItems: DropdownItem<ValueType>[] = [];

    /**
     * Dropdown items generated of dropdownButton
     * To manipulate it, use dropDownButtonConfig and refresh component
     */
    public get dropDownItems(): DropdownItem<ValueType>[] {
        return this._dropDownItems;
    }

    /** Value of the current selected  */
    private _value?: ValueType;

    /** Bootstrap instance of the dropdown button */
    private _bsDropdown!: Dropdown;

    /** @returns bootstrap instance of button */
    public get bsDropdown(): Dropdown { return this._bsDropdown; }

    constructor(options?: Partial<DropdownButtonOptions<ValueType>>, customElementOptions?: OverrideCustomElementConfig) {
        super(options, customElementOptions);

        // Define default getDropdownItemParent, if not given in options of constructor
        if (!options?.getDropdownItemParent) {
            this.options.setWithoutRefresh({
                getDropdownItemParent: child => this.options.dropDownItemConfigs.find(item => child.parentRef && child.parentRef === item.refAttribute)
            });
        }
    }

    /**
     * Set dropdown items configuration
     */
    public setDropdownItems(dropdownItems: DropdownItemConfig<ValueType>[]): void {
        this.options.setWithoutRefresh({ dropDownItemConfigs: dropdownItems });
        this.refreshDropdownButtonContent();
    }

    /** When component init */
    public override init(): void {
        super.init();

        // Handle menu open event
        this.addEventListener("show.bs.dropdown", _ => {
            if (!Boolean(this.options.openMenuInBody)) { return; }// Useless to process open in body

            const menu = this.components.dropdownMenu;
            document.body.append(menu);
        });
    }

    /**
     * Refresh component
     */
    public override refresh(): void {
        // If there's no icon, label is still displayed
        if (this.options.iconOnly && this.options.icon) {
            this.options.setWithoutRefresh({ label: "", iconMode: "only" });
        }

        super.refresh();

        this.refreshDropdownButtonContent();

        toggleClass(this.components.button, this.options.arrow && !this.options.splitToggleArrow, "dropdown-toggle");

        toggleVisible(this.components.splitButton, this.options.splitToggleArrow, Display.BLOCK);

        if (this.options.splitToggleArrow) {
            // Style class
            let styleClass = BTN_CLASS;
            let shouldAddClass = false;

            // Outline
            if (this.options.outline) {
                styleClass += BTN_CLASS_SEPARATOR + BTN_OUTLINE;
                shouldAddClass = true;
            }

            if (this.options.cssStyle !== ButtonStyle.NONE) {
                styleClass += BTN_CLASS_SEPARATOR + this.options.cssStyle;
                shouldAddClass = true;
            }

            if (shouldAddClass) {
                this.components.splitButton.classList.add(styleClass);
            }

            toggleVisible(this.components.splitButton, this.options.splitToggleArrow, Display.BLOCK);
            toggleClass(this, this.options.splitToggleArrow, "btn-group");
        }

        // if there is no split button, clicking on the "main" button shows the dropdown
        toggleAttribute(this.components.button, !this.options.splitToggleArrow && this.options.dropDownItemConfigs.length > 0, "data-bs-toggle", "dropdown");

        // Set raised
        toggleClass(this.components.splitButton, this.options.raised, BTN_RAISED);

        // Set rounded
        toggleClass(this.components.button, this.options.rounded, BTN_ROUNDED);

        // Toggle dark menu
        toggleClass(this.components.dropdownMenu, this.options.darkMenu, "dropdown-menu-dark");

        if (this.options.dropDirection) {
            switch (this.options.dropDirection) {
                case "top":
                    this.classList.add("dropup");
                    break;
                case "left":
                    this.classList.add("dropstart");
                    break;
                case "right":
                    this.classList.add("dropend");
                    break;

                // Bottom is default behavior
                case "bottom":
                default:
                    break;
            }
        }
        toggleClass(this.components.dropdownMenu, this.options.dropDownMenuAlign === "right", "dropdown-menu-end");
    }

    /** @inheritdoc */
    public override connectedCallback(): void {
        super.connectedCallback();

        // Only create bootstrap instance when component is added to dom
        this._bsDropdown = new Dropdown(this.components.button, this.options.bsDropDrownOptions);
    }

    /** @inheritdoc */
    public override disconnectedCallback(): void {
        super.disconnectedCallback();

        // Remove dropdown instance
        this._bsDropdown.dispose();

        // If menu is in document body
        if (document.body.contains(this.components.dropdownMenu)) {
            // Don't forget to remove menu when button is removed from dom
            this.components.dropdownMenu.remove();
        }
    }

    /**
     * Refresh dropdown button content
     */
    public refreshDropdownButtonContent(): void {
        // Empty menu
        empty(this.components.dropdownMenu);

        // Empty the tree
        this.dropDownItems.length = 0;

        // Refresh the configs tree
        this._buildConfigsTree();

        // Refresh the items
        this._buildItems();
    }

    /**
     * Build the config tree
     */
    private _buildConfigsTree(): void {
        // Clear configs cache
        this.options.dropDownItemConfigs.forEach(config => setProp(config, DROPDOWN_CONFIG_CHILD_ATTRIBUTE, undefined));

        // Fill tree
        for (const config of this.options.dropDownItemConfigs) {
            const dropdownParentConfig = this.options.getDropdownItemParent?.(config);
            setProp(config, DROPDOWN_CONFIG_PARENT_ATTRIBUTE, Boolean(dropdownParentConfig));

            // Useless to assign child attribute if parent config isn't found
            if (!dropdownParentConfig) { continue; }

            // Add config into parentConfig cache attribute
            if (!getProp(dropdownParentConfig, DROPDOWN_CONFIG_CHILD_ATTRIBUTE)) { setProp(dropdownParentConfig, DROPDOWN_CONFIG_CHILD_ATTRIBUTE, []); }
            getProp<DropdownItemConfig<ValueType>[]>(dropdownParentConfig, DROPDOWN_CONFIG_CHILD_ATTRIBUTE).push(config);
        }
    }

    /**
     * Build the root of items
     */
    private _buildItems(): void {
        // Root items are items who don't have any parent
        const rootItems = this.options.dropDownItemConfigs.filter(itemConfig => !getProp(itemConfig, DROPDOWN_CONFIG_PARENT_ATTRIBUTE));
        this._buildDropdownList(rootItems, { isRoot: true });
    }

    /**
     * Build dropdown list
     */
    protected _buildDropdownList(dropdownItems: DropdownItemConfig<ValueType>[], options?: { isRoot?: boolean }): HTMLUListElement {
        const ulElement = options?.isRoot ? this.components.dropdownMenu : createHtmlElement("ul", { cssClass: "dropdown-menu" });

        for (const dropdownItem of dropdownItems) {
            // Don't create and add item if hide is required
            if (dropdownItem.hide) { continue; }

            const dropdownItemBuilded = this._buildDropdownItem(dropdownItem);
            this._dropDownItems.push(dropdownItemBuilded);

            // Append to menu
            if (dropdownItemBuilded.html) {
                ulElement.append(dropdownItemBuilded.html);
            }

            // Add separator divider line under button
            if (dropdownItemBuilded.separator) {
                ulElement.append(this._buildSeparator());
            }
        }

        return ulElement;
    }

    /** 
     * Build dropdown item 
     */
    protected _buildDropdownItem(itemConfig: DropdownItemConfig<ValueType>): DropdownItem<ValueType> {
        const li = createHtmlElement("li");
        let i: HTMLElement | undefined;

        const dropDownItem: DropdownItem<ValueType> = {
            ...itemConfig,
            depth: 0,
            dropDownItems: []
        };

        if (dropDownItem.icon) {
            i = buildIcon(dropDownItem.icon);
            i.classList.add("dropdown-icon");
        }

        const a = createHtmlElement("a", { cssClass: ["dropdown-item"], innerHTML: i ? [i, dropDownItem.label] : [dropDownItem.label] });

        if (dropDownItem.href) { a.href = "#" + dropDownItem.href; }
        if (dropDownItem.refAttribute) { li.setAttribute(REF_ATTRIBUTE, dropDownItem.refAttribute); }

        if (dropDownItem.title) { li.setAttribute("title", dropDownItem.title); }

        li.classList.add(...dropDownItem.classes ?? []);

        // Retrieve list of subConfigs of for this item
        const subItems = getProp<DropdownItem<ValueType>[]>(dropDownItem, DROPDOWN_CONFIG_CHILD_ATTRIBUTE) ?? [];

        // Check if item is disabled
        const isItemDisabled = getVOFValue(dropDownItem.disabled);
        if (isItemDisabled || subItems.length) {
            li.addEventListener("click", (e) => { e.stopImmediatePropagation(); });
        }

        // Toggle disabled class
        toggleClass(a, isItemDisabled, "disabled");

        if (!isItemDisabled) {
            if (subItems.length) {
                const subIconClasses: string[] = [];
                if (itemConfig.subMenuIcon) {
                    subIconClasses.push(...[itemConfig.subMenuIcon.prefix ?? FaClasses.SOLID, itemConfig.subMenuIcon.name, "dropdown-multi-level-icon"]);
                    if (itemConfig.subMenuIcon.fixedWidth) { subIconClasses.push(FA_FW); }
                    if (itemConfig.subMenuIcon.classes) { subIconClasses.push(...itemConfig.subMenuIcon.classes); }
                } else {
                    const iconName = this.options.subMenuDropDirection === "right" ? IconFA6.CHEVRON_RIGHT : IconFA6.CHEVRON_LEFT;
                    subIconClasses.push(...["fa", getIconFaName(iconName), IconFaSize.XS2, "dropdown-multi-level-icon"]);
                }

                const subIconElement = createHtmlElement("i", {
                    cssClass: subIconClasses,
                    attributes: {
                        ref: "icon",
                        ariaHidden: "true"
                    }
                });
                li.classList.add("dropdown-submenu");
                if (this.options.subMenuDropDirection === "right") {
                    a.append(subIconElement);
                    li.classList.add("dropEnd");
                }
                else {
                    a.prepend(subIconElement);
                    li.classList.add("dropStart");
                }

                const ulChild = this._buildDropdownList(subItems);
                li.append(ulChild);

                li.addEventListener("mouseenter", () => toggleVisible(ulChild, true, Display.BLOCK));
                li.addEventListener("mouseleave", () => toggleVisible(ulChild, false, Display.BLOCK));
            } else {
                a.addEventListener("click", () => {
                    if (isItemDisabled) { return; }

                    this.itemClickEvent.emit({ value: getVOFValue(itemConfig.value) });

                    if (this.options.enableSelectBehavior) {
                        if (!this.options.iconOnly || !itemConfig.icon) { // if there's no icon, button's label is still set
                            this.options.setWithoutRefresh({ label: itemConfig.label });
                        }
                        if (itemConfig.icon) {
                            this.options.setWithoutRefresh({ icon: { ...itemConfig.icon, fixedWidth: true }, iconMode: this.options.iconOnly ? "only" : "before" });
                        } else { // if there's no icon to be displayed in dropdown item, remove component item
                            this.options.setWithoutRefresh({ icon: undefined });
                        }
                        this._bsDropdown.hide();
                        if (this.options.splitToggleArrow) {
                            // if we don't have splitToggleArrow option, we must not disable the "main item" otherwise we can't show the options
                            this.options.setWithoutRefresh({ disabled: Boolean(getVOFValue(itemConfig.disabled)) });
                        }

                        this.refresh();
                    }

                    if (itemConfig.value) {
                        this._value = getVOFValue(itemConfig.value);
                    }
                });
            }
        }

        li.append(a);
        dropDownItem.html = li;
        return dropDownItem;
    }

    /** Returns the current selected item type */
    public getValue(): ValueType | undefined {
        return this._value;
    }

    /**
     * Change dropdownButton's value if possible
     * @param value new value for dropdownButton 
     * @returns true if this._value changed
     */
    public setValue(value: ValueOrFunction<ValueType>): boolean {
        if (this.options.dropDownItemConfigs?.length) {

            const rawValue = getVOFValue(value);

            // For each item of the dropdownButton
            for (const item of this.options.dropDownItemConfigs) {
                // set value only if this value exist in one item
                if (getVOFValue(item.value) === rawValue) {
                    this._value = rawValue;

                    // If select behavior is enable, label displayed in select should be current value
                    // If item value is equal to current value, set item label as text of dropDown
                    if (this.options.enableSelectBehavior && this._value != null) {
                        // Refresh selected item label
                        this.options.setAndRefreshIfChanged({ label: item.label });
                    }

                    this.refresh();
                    return true;
                }
            }
        }
        return false;
    }

    /** Build separator */
    protected _buildSeparator(): HTMLLIElement {
        return createHtmlElement("li", { cssClass: ["dropdown-divider"], innerHTML: document.createElement("hr") });
    }

    /**
     * Init component listeners
     */
    protected override _initListeners(): void {
        super._initListeners();
    }

    /** Refresh button and splitArrow size, based on options.size */
    protected override _refreshSize(): void {
        const defaultSize = this.options.defaultSize ?? defaultButtonOptions.defaultSize!;

        if (typeof this.options.size === "number") {// Custom size defined (number type)

            // Apply scale factor in options to baseDimension of button
            const height = toPx(this.options.size * defaultSize);
            const width = toPx(this.options.size * defaultSize * 2);// Admit than btn is 2x time larger than taller

            setHTMLElementStyle(this.button, { height, width });
            setHTMLElementStyle(this.components.splitButton, { height, width });
            // ButtonSize size defined (check if value exist in enum)
        } else if (Object.values(ButtonSize).includes(this.options.size as ButtonSize)) {


            // Get scale value from enum in options
            switch (this.options.size) {
                case ButtonSize.LARGE:
                    this.button.classList.add("btn-lg");
                    this.components.splitButton.classList.add("btn-lg");
                    break;
                case ButtonSize.SMALL:
                    this.button.classList.add("btn-sm");
                    this.components.splitButton.classList.add("btn-sm");
                    break;
                case ButtonSize.NORMAL:
                default:
                    break;
            }

        } else {// Custom size defined (string type)  (ex: 4em / 20px / 1vh / etc.)
            // Set the width
            const width = this.options.size ?? ButtonSize.NORMAL;
            setHTMLElementStyle(this.button, { width });
            setHTMLElementStyle(this.components.splitButton, { width });

            if (this.options.fab) {// Set height also if it's a fab btn (round)
                const height = this.options.size ?? ButtonSize.NORMAL;
                setHTMLElementStyle(this.button, { height });
                setHTMLElementStyle(this.components.splitButton, { height });
            }
        }
    }

    /**
     * Used by Navigation package when building menus 
     * Does dropdownMenu.insertBefore(itemElement, insertBefore)
     */
    public insertMenuItem(itemElement: HTMLElement, insertBefore: HTMLElement | null): void {
        this.components.dropdownMenu.insertBefore(itemElement, insertBefore);
    }

    /**
     * Setup the menu item in order to navigate to url on click
     * Used by Navigation package when building menus 
     */
    public setUrl(url: string): void {
        const NAVIGATE_ON_CLICK_INSTALLED = "NAVIGATE_ON_CLICK_INSTALLED";
        this.setAttribute("href", url);// save href as an attribute
        if (!getProp(this, NAVIGATE_ON_CLICK_INSTALLED)) {
            setProp(this, NAVIGATE_ON_CLICK_INSTALLED, true);
            // install event if not already installed
            this.clickEvent.on(() => {
                const hRef = this.getAttribute("href");
                if (hRef) {
                    location.href = hRef;
                }
            });
        }
    }
}
